import type { Theme } from '@mui/material';

import { Z_INDEX_LAYERS } from 'src/constants/settings';

export const SIDE_BAR_DRAWER_Z_INDEX = 1200;

export const styles = {
  pageFrame: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },

  contentContainer: (theme: Theme) => ({
    height: '100%',
    maxHeight: `calc(100% - ${theme.dimension.headerHeight.xs}px)`,
    overflow: 'hidden',

    [theme.breakpoints.up('lg')]: {
      maxHeight: `calc(100% - ${theme.dimension.headerHeight.lg}px)`,
    },
  }),

  collapsed: (theme: Theme) => ({
    '& .PageFrame-sidebar': {
      marginLeft: `calc(-25% + ${theme.spacing(2)})`,
    },

    '& .PageFrame-main': {
      maxWidth: `calc(100% - ${theme.spacing(2)})`,
    },
  }),

  sidebar: (theme: Theme) => ({
    borderRight: `2px solid ${theme.palette.background.grey}`,
    maxHeight: '100%',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    transition: 'margin-left 0.1s ease-in-out',
  }),

  collapseButton: (theme: Theme) => ({
    backgroundColor: theme.palette.background.default,
    top: '50%',
    right: theme.spacing(-2),
    transform: 'translateY(-50%)',
    position: 'absolute',
    zIndex: 1, // Makes full button clickable again despite being pushed out of its parent boundaries.
  }),

  sidebarContentContainer: {
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },

  sidebarDrawer: (theme: Theme) => ({
    '& .MuiDrawer-paper': {
      minWidth: '100%',
      maxWidth: '100%',
      padding: 2,
      zIndex: SIDE_BAR_DRAWER_Z_INDEX,

      [theme.breakpoints.up('sm')]: {
        minWidth: 350,
        maxWidth: 350,
      },
    },
  }),

  main: {
    maxHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,

    // Override media query styles by specificity.
    '&.MuiGrid-root': {
      flexGrow: 1,
    },
  },

  infoBanner: {
    pb: 1,
  },

  children: (pageFrameWidth?: number) => () => ({
    height: '100%',
    pt: 2.25,
    pb: 2.25,
    ...(pageFrameWidth
      ? {
          // Fixed page width
          scrollbarGutter: 'stable both-edges',
          '> *': {
            margin: '0 auto',
            maxWidth: `${pageFrameWidth}px`,
          },
        }
      : {
          // Max-width page with paddings
          pr: 3.5,
          pl: 4,
        }),
    overflow: 'auto',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  }),

  actionBar: {
    position: 'fixed',
    px: '1.25rem',
    right: 0,
    left: 0,
    bottom: 0,
    zIndex: Z_INDEX_LAYERS.FRONT.BASE,
    justifyContent: 'flex-end',
    pointerEvents: 'none',
  },

  downloadManager: {
    overflow: 'hidden',
    justifyContent: 'flex-end',
    pointerEvents: 'auto',
  },

  chatBotWrapper: {
    justifyContent: 'flex-end',
    pb: 2,
    pointerEvents: 'auto',
  },
};
